import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Separator } from 'components/ui/separator';
import { Switch } from 'components/ui/switch';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { useToast } from 'hooks/use-toast';
import { Check, CodeXml, Copy, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard } from 'lib/utils';
import { PlatformIntegrationCard } from './platform-integration-card';
import type { ChatbotWidget } from './types';

const switchClass =
  'h-5 w-9 border-transparent bg-[#e7e7e7] data-[state=checked]:bg-black dark:data-[state=checked]:bg-foreground [&_[data-state]]:size-4 [&_[data-state=checked]]:translate-x-[16px] [&_[data-state=unchecked]]:translate-x-0.5';

type AgentIntegrationChatbotScriptProps = {
  widgetId: string;
  projectKey: string;
  appDomainName: string;
  onAllowedDomainsInputChange: (value: string) => void;
  onRegenerateWidgetId: () => Promise<void>;
  isRegeneratingWidgetId?: boolean;
  canEdit?: boolean;
  showSave?: boolean;
};

const buildEmbedTag = (widgetUrl: string, widgetId: string, projectKey: string) =>
  `<script
  src="${widgetUrl}/embed.js"
  data-widget-id="${widgetId}"
  data-project-key="${projectKey}"
></script>`;

const buildSpaAuthScript = (embedTag: string) =>
  `<script>
  window.SeliseWidgetConfig = {
    opaque: true,
    getToken: async function (isExpired) {
      // Return a JWT string, or null for anonymous visitors.
      if (isExpired) return await yourAuth.refreshToken();
      return await yourAuth.getAccessToken();
    },
  };
</script>
${embedTag}`;

const buildCookieAuthScript = (embedTag: string) =>
  `<script>
  (function () {
    // Replace these 3 values with your host app's auth.
    var TOKEN_URL = "/YOUR_TOKEN_ENDPOINT";
    var CLIENT_ID = "YOUR_CLIENT_ID";
    var TOKEN_COOKIE = "YOUR_ACCESS_TOKEN_COOKIE";

    function cookie(name) {
      var m = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/\\./g, "\\\\.") + "=([^;]*)")
      );
      return m ? decodeURIComponent(m[1]) : null;
    }

    function isFresh(jwt) {
      try {
        var exp = JSON.parse(
          atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        ).exp;
        return exp * 1000 > Date.now() + 30000;
      } catch (e) {
        return false;
      }
    }

    async function refresh() {
      var rt = cookie("REFRESH_TOKEN");
      if (!rt) return null;
      var res = await fetch(TOKEN_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body:
          "grant_type=refresh_token&client_id=" +
          CLIENT_ID +
          "&refresh_token=" +
          encodeURIComponent(rt),
      });
      if (!res.ok) return null;
      return (await res.json()).access_token || null;
    }

    window.SeliseWidgetConfig = {
      opaque: true,
      getToken: async function (isExpired) {
        if (!isExpired) {
          var token = cookie(TOKEN_COOKIE);
          if (token && isFresh(token)) return token;
        }
        return refresh();
      },
    };
  })();
</script>
${embedTag}`;

const InlineCode = ({ children }: { children: ReactNode }) => (
  <code className="rounded bg-muted px-1">{children}</code>
);

type EmbedOptionCardProps = {
  option: string;
  title: string;
  hint: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

const EmbedOptionCard = ({ option, title, hint, actions, children }: EmbedOptionCardProps) => (
  <div className="flex w-full flex-col gap-3 rounded-xl border border-[#e7e7e7] bg-[#fafafa] p-4 dark:border-border dark:bg-muted/30">
    <div className="flex w-full items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2.5">
        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-black text-xs font-semibold leading-none text-white dark:bg-foreground dark:text-background">
          {option}
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <h4 className="text-sm font-semibold leading-[1.3] text-[#3d3d3d] dark:text-foreground">
            {title}
          </h4>
          <p className="text-xs font-normal leading-[1.4] text-[#6d6d6d] dark:text-muted-foreground">
            {hint}
          </p>
        </div>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

type PublishStepProps = {
  step?: number;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
};

const PublishStep = ({ step, title, description, actions, children }: PublishStepProps) => (
  <div className="flex w-full gap-4">
    {step != null ? (
      <div
        className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-black text-sm font-semibold leading-none text-white dark:bg-foreground dark:text-background"
        aria-hidden
      >
        {step}
      </div>
    ) : (
      <div className="size-7 shrink-0" aria-hidden />
    )}
    <div className="flex min-w-0 flex-1 flex-col gap-2.5">
      <div className="flex flex-col gap-1">
        <div className="flex w-full items-start justify-between gap-3">
          <h3 className="text-base font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
            {title}
          </h3>
          {actions}
        </div>
        {description ? (
          <p className="text-xs font-normal leading-[1.4] text-[#6d6d6d] dark:text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  </div>
);

export const AgentIntegrationChatbotScript = ({
  widgetId,
  projectKey,
  onAllowedDomainsInputChange,
  onRegenerateWidgetId,
  isRegeneratingWidgetId = false,
  canEdit = true,
  showSave = false,
}: AgentIntegrationChatbotScriptProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isCopying, setIsCopying] = useState(false);
  const [copiedKey, setCopiedKey] = useState<'embed' | 'spa' | 'cookie' | null>(null);
  const form = useFormContext<ChatbotWidget>();
  const { isValid, isSubmitting } = form.formState;

  const integrations = useMemo(
    () => [
      {
        name: 'Slack',
        description: t('SLACK_INTEGRATION_DESCRIPTION'),
        imageUrl: '/assets/images/agents/slack.png',
      },
      {
        name: 'Teams',
        description: t('TEAMS_INTEGRATION_DESCRIPTION'),
        imageUrl: '/assets/images/agents/teams.png',
      },
      {
        name: 'Discord',
        description: t('DISCORD_INTEGRATION_DESCRIPTION'),
        imageUrl: '/assets/images/agents/discord.png',
      },
      {
        name: 'WhatsApp',
        description: t('WHATSAPP_INTEGRATION_DESCRIPTION'),
        imageUrl: '/assets/images/agents/whatsapp.png',
      },
      {
        name: 'Telegram',
        description: t('TELEGRAM_INTEGRATION_DESCRIPTION'),
        imageUrl: '/assets/images/agents/telegram.png',
      },
    ],
    [t]
  );

  const widgetUrl = import.meta.env.VITE_WIDGET_URL || '';

  const scriptContent = buildEmbedTag(widgetUrl, widgetId, projectKey);
  const spaAuthScript = buildSpaAuthScript(scriptContent);
  const cookieAuthScript = buildCookieAuthScript(scriptContent);
  const standaloneUrl = `${widgetUrl}/widget/standalone?widget_id=${widgetId}&project_key=${projectKey}`;

  const copySnippet = async (
    event: MouseEvent<HTMLButtonElement>,
    key: 'embed' | 'spa' | 'cookie',
    value: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (isCopying) return;

    setIsCopying(true);
    try {
      await copyTextToClipboard(value);
      setCopiedKey(key);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('CODE_COPIED_TO_CLIPBOARD'),
      });
    } catch {
      toast({
        title: t('ERROR'),
        description: t('FAILED_TO_COPY_CODE'),
        variant: 'destructive',
      });
    } finally {
      setTimeout(() => {
        setIsCopying(false);
      }, 500);
    }
  };

  return (
    <div className="flex w-full flex-col gap-8 pb-4">
      <div className="flex w-full flex-col gap-8">
        <PublishStep
          step={1}
          title={t('ALLOW_THIS_DOMAIN', { defaultValue: 'Allow this domain' })}
          description={
            t('ALLOW_THIS_DOMAIN_DESCRIPTION') ||
            'Enter the domain(s) where you will embed the chatbot, separated by commas (for example selise.ai, seliseblocks.com). The widget stays disabled until you set where it will be embedded.'
          }
        >
          <FormField
            control={form.control}
            name="allowed_domains"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col gap-2.5 space-y-0">
                <FormLabel className="sr-only">
                  {t('ALLOWED_DOMAINS') || 'Allowed Domains'}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      onAllowedDomainsInputChange(event.target.value);
                    }}
                    placeholder={
                      t('ALLOWED_DOMAINS_PLACEHOLDER') || 'selise.ai, w3schools.com'
                    }
                    className="h-12 rounded-lg border-[#e7e7e7] bg-white px-5 py-4 text-base font-normal leading-[1.6] text-[#3d3d3d] shadow-none placeholder:text-[#b0b0b0] focus-visible:ring-1 dark:border-border dark:bg-card dark:text-foreground"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PublishStep>

        <PublishStep
          title={
            t('ALLOW_VISITOR_TOOL_CONNECT') ||
            'Allow visitors to connect their own accounts'
          }
          description={
            t('ALLOW_VISITOR_TOOL_CONNECT_HINT') ||
            'When off, visitors cannot sign in to connect tool accounts from this chat. Tools using the host session token are unaffected.'
          }
          actions={
            <FormField
              control={form.control}
              name="allow_visitor_tool_connect"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canEdit}
                      className={switchClass}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          }
        />

        <FormField
          control={form.control}
          name="host_cookie_name"
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-2.5 space-y-0">
              <FormLabel className="text-base font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
                {t('HOST_COOKIE_NAME') || 'Read visitor session from cookie'}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder="e.g. sunrise_sid"
                  className="h-12 rounded-lg border-[#e7e7e7] bg-white px-5 py-4 text-base font-normal leading-[1.6] text-[#3d3d3d] shadow-none placeholder:text-[#b0b0b0] focus-visible:ring-1 dark:border-border dark:bg-card dark:text-foreground"
                />
              </FormControl>
              <p className="text-xs font-normal leading-[1.4] text-[#6d6d6d] dark:text-muted-foreground">
                {t('HOST_COOKIE_NAME_HINT') ||
                  "Cookie relay for widgets hosted on a sibling subdomain of your site: this cookie's value becomes the visitor's token for Host-session tools. The cookie must be parent-domain. Leave empty to disable."}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <PublishStep
          step={2}
          title={t('COPY_THE_EMBED_SCRIPT', { defaultValue: 'Copy the embed script' })}
          description={
            t('COPY_THE_EMBED_SCRIPT_DESCRIPTION') ||
            'Choose one option — not both. Use no access token for public chat. Use an access token only if the chatbot must call your app\'s resources as tools.'
          }
          actions={
            canEdit ? (
              // Enabled with no id too: that is how this org mints its first one.
              <Button
                variant="ghost"
                className="size-6 shrink-0 p-0 text-[#3d3d3d] hover:bg-transparent hover:text-[#3d3d3d] dark:text-foreground [&_svg]:size-5"
                type="button"
                disabled={isRegeneratingWidgetId}
                onClick={() => {
                  void onRegenerateWidgetId();
                }}
                aria-label={t('REGENERATE_WIDGET_ID') || 'Regenerate widget ID'}
                title={t('REGENERATE_WIDGET_ID') || 'Regenerate widget ID'}
              >
                {isRegeneratingWidgetId ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              </Button>
            ) : null
          }
        >
          <div className="flex w-full flex-col gap-3">
            <EmbedOptionCard
              option="A"
              title={t('NO_ACCESS_TOKEN', { defaultValue: 'No access token' })}
              hint={
                t('NO_ACCESS_TOKEN_HINT') ||
                "Paste this script before your page's closing body tag. Visitors do not need to be logged in."
              }
              actions={
                canEdit ? (
                  <Button
                    variant="ghost"
                    className="size-6 shrink-0 p-0 text-[#3d3d3d] hover:bg-transparent hover:text-[#3d3d3d] dark:text-foreground [&_svg]:size-5"
                    type="button"
                    disabled={isCopying || isRegeneratingWidgetId}
                    onClick={(event) => void copySnippet(event, 'embed', scriptContent)}
                    aria-label={t('COPY_PUBLIC_EMBED_SCRIPT') || 'Copy public embed script'}
                  >
                    {copiedKey === 'embed' ? <Check /> : <Copy />}
                  </Button>
                ) : null
              }
            >
              <pre className="overflow-auto whitespace-pre-wrap break-words rounded-lg border border-[#e7e7e7] bg-white px-4 py-3 font-mono text-sm font-normal leading-[1.6] text-[#3d3d3d] dark:border-border dark:bg-card dark:text-foreground">
                {scriptContent}
              </pre>
            </EmbedOptionCard>

            <div className="flex items-center gap-3 px-1">
              <Separator className="flex-1 bg-[#e7e7e7] dark:bg-border" />
              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-[#888888]">
                {t('OR')}
              </span>
              <Separator className="flex-1 bg-[#e7e7e7] dark:bg-border" />
            </div>

            <EmbedOptionCard
              option="B"
              title={t('ACCESS_TOKEN_FROM_YOUR_APP', {
                defaultValue: 'Access token from your app',
              })}
              hint={
                <>
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_PREFIX', {
                    defaultValue:
                      "Use this when the chatbot must call your app's resources as tools. Keep",
                  })}{' '}
                  <InlineCode>window.SeliseWidgetConfig</InlineCode>{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_ABOVE', { defaultValue: 'above' })}{' '}
                  <InlineCode>embed.js</InlineCode>.{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_REPLACE', {
                    defaultValue: 'Replace',
                  })}{' '}
                  <InlineCode>yourAuth.getAccessToken()</InlineCode>{' '}
                  {t('AND', { defaultValue: 'and' })}{' '}
                  <InlineCode>yourAuth.refreshToken()</InlineCode>{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_WITH_HELPERS', {
                    defaultValue: 'with your auth helpers.',
                  })}{' '}
                  <InlineCode>getToken()</InlineCode>{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_MUST_RETURN', {
                    defaultValue: 'must return a JWT, or',
                  })}{' '}
                  <InlineCode>null</InlineCode>{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_IF_NOT_LOGGED_IN', {
                    defaultValue: 'if the visitor is not logged in. When',
                  })}{' '}
                  <InlineCode>isExpired</InlineCode>{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_IS', { defaultValue: 'is' })}{' '}
                  <InlineCode>true</InlineCode>,{' '}
                  {t('ACCESS_TOKEN_FROM_YOUR_APP_HINT_REFRESH', {
                    defaultValue: 'return a refreshed token.',
                  })}
                </>
              }
              actions={
                canEdit ? (
                  <Button
                    variant="ghost"
                    className="size-6 shrink-0 p-0 text-[#3d3d3d] hover:bg-transparent hover:text-[#3d3d3d] dark:text-foreground [&_svg]:size-5"
                    type="button"
                    disabled={isCopying}
                    onClick={(event) => void copySnippet(event, 'spa', spaAuthScript)}
                    aria-label={
                      t('COPY_ACCESS_TOKEN_EMBED_SCRIPT') || 'Copy access token embed script'
                    }
                  >
                    {copiedKey === 'spa' ? <Check /> : <Copy />}
                  </Button>
                ) : null
              }
            >
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-[#e7e7e7] bg-white px-4 py-3 font-mono text-sm font-normal leading-[1.6] text-[#3d3d3d] dark:border-border dark:bg-card dark:text-foreground">
                {spaAuthScript}
              </pre>
            </EmbedOptionCard>
          </div>
        </PublishStep>

        <PublishStep
          title={t('EXAMPLE_ACCESS_TOKEN_FROM_COOKIES', {
            defaultValue: 'Example — access token from cookies',
          })}
          description={
            <>
              {t('COOKIE_AUTH_HINT_PREFIX', {
                defaultValue:
                  "Use this when the chatbot needs an access token to call your app's resources as tools, and that token lives in cookies. This is an alternative to option B. Keep",
              })}{' '}
              <InlineCode>window.SeliseWidgetConfig</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_ADDED_ABOVE', { defaultValue: 'added above' })}{' '}
              <InlineCode>embed.js</InlineCode>.{' '}
              {t('COOKIE_AUTH_HINT_REPLACE', { defaultValue: 'Replace' })}{' '}
              <InlineCode>YOUR_TOKEN_ENDPOINT</InlineCode>, <InlineCode>YOUR_CLIENT_ID</InlineCode>,{' '}
              {t('AND', { defaultValue: 'and' })}{' '}
              <InlineCode>YOUR_ACCESS_TOKEN_COOKIE</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_WITH_HOST_VALUES', {
                defaultValue: 'with your host app values.',
              })}{' '}
              <InlineCode>getToken()</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_REUSES', {
                defaultValue: 'reuses the access-token cookie while it is still valid. When',
              })}{' '}
              <InlineCode>isExpired</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_IS', { defaultValue: 'is' })} <InlineCode>true</InlineCode>,{' '}
              {t('COOKIE_AUTH_HINT_OR_STALE', {
                defaultValue: 'or the cookie is missing or stale, it refreshes with the',
              })}{' '}
              <InlineCode>REFRESH_TOKEN</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_COOKIE', { defaultValue: 'cookie. Returns' })}{' '}
              <InlineCode>null</InlineCode>{' '}
              {t('COOKIE_AUTH_HINT_IF_NOT_LOGGED_IN', {
                defaultValue: 'if the visitor is not logged in.',
              })}
            </>
          }
          actions={
            canEdit ? (
              <Button
                variant="ghost"
                className="size-6 shrink-0 p-0 text-[#3d3d3d] hover:bg-transparent hover:text-[#3d3d3d] dark:text-foreground [&_svg]:size-5"
                type="button"
                disabled={isCopying}
                onClick={(event) => void copySnippet(event, 'cookie', cookieAuthScript)}
                aria-label={t('COPY_COOKIE_EXAMPLE') || 'Copy cookie example'}
              >
                {copiedKey === 'cookie' ? <Check /> : <Copy />}
              </Button>
            ) : null
          }
        >
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-[#e7e7e7] bg-white px-5 py-4 font-mono text-sm font-normal leading-[1.6] text-[#3d3d3d] dark:border-border dark:bg-card dark:text-foreground">
            {cookieAuthScript}
          </pre>
        </PublishStep>

        <div className="flex w-full flex-col gap-2.5">
          <h3 className="text-base font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
            {t('OPEN_THE_STANDALONE_CHAT_VIEW', {
              defaultValue: 'Open the standalone chat view',
            })}
          </h3>
          <p className="text-xs font-normal leading-[1.4] text-[#6d6d6d] dark:text-muted-foreground">
            {t('OPEN_THE_STANDALONE_CHAT_VIEW_HINT') ||
              'Use this link or the QR code below to try the standalone chat view.'}
          </p>
          <div className="flex w-full flex-col items-stretch gap-4 sm:flex-row sm:items-start">
            <Input
              value={standaloneUrl}
              readOnly
              className="h-12 min-w-0 flex-1 truncate rounded-lg border-[#e7e7e7] bg-white px-5 py-4 text-base font-normal leading-[1.6] text-[#3d3d3d] shadow-none focus-visible:ring-0 dark:border-border dark:bg-card dark:text-foreground"
            />
            {canEdit ? (
              <Link to={standaloneUrl} target="_blank" className="shrink-0">
                <Button
                  type="button"
                  className="h-12 w-full rounded-[62px] border-0 bg-black px-8 py-4 text-base font-semibold leading-[1.15] text-white shadow-none sm:w-auto dark:bg-foreground dark:text-background dark:hover:opacity-90"
                >
                  {t('OPEN')}
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-7">
        <div className="flex w-full items-center gap-7">
          <Separator className="flex-1 bg-[#e7e7e7] dark:bg-border" />
          <span className="shrink-0 text-lg font-semibold leading-[1.5] text-[#888888]">
            {t('OR')}
          </span>
          <Separator className="flex-1 bg-[#e7e7e7] dark:bg-border" />
        </div>

        <div className="flex w-full flex-col border-b border-[#e7e7e7] pb-8 dark:border-border">
          <div className="flex w-full flex-col gap-5">
            <h3 className="text-lg font-semibold leading-[1.5] text-[#3d3d3d] dark:text-foreground">
              {t('SCAN_THE_CODE')}
            </h3>
            <div className="size-[120px]">
              <QRCode
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={standaloneUrl}
              />
            </div>
          </div>
        </div>

        {/* <div className="flex w-full flex-col gap-5">
          <div className="flex items-center gap-2">
            <h3 className="flex items-center gap-2 text-lg font-semibold leading-[1.5] text-[#3d3d3d] dark:text-foreground">
              <CodeXml className="size-5 shrink-0" aria-hidden />
              {t('OTHER_INTEGRATIONS')}
            </h3>
            <Badge className="rounded-full border-transparent bg-[#f79009] px-3 py-1 text-xs font-semibold leading-[1.2] text-white hover:bg-[#f79009]">
              {t('COMING_SOON')}
            </Badge>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {integrations.map((item) => (
              <PlatformIntegrationCard key={item.name} {...item} />
            ))}
          </div>
        </div> */}
      </div>

      {showSave ? (
        <div className="border-t border-[#e7e7e7] pt-4 dark:border-border">
          <Button
            className="h-12 w-full rounded-[62px] text-base font-semibold"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? t('SAVING', { defaultValue: 'Saving...' }) : t('SAVE')}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
